import { useState } from 'react';
import * as Yup from 'yup';
import { toast } from 'sonner';

import { useSelector } from 'react-redux';
import {
  selectUserInfo,
  selectUserToken,
} from '../../../../../redux/features/user/userSelectors';
import { useGetClientQuery } from '../../../../../redux/services/clients/clientsApi';
import { useSendMailingMutation } from '../../../../../redux/services/emails/emailsApi';
import { useEmailContext } from '../../../../../contexts/EmailContext';

import { FormTemplate } from '../../FormTemplate';
import { EmailFormInputs } from '../EmailFormInputs';
import { EmailVariables } from '../EmailVariables';
import { LoaderProgress } from '../../../../ui/LoaderProgress';

import {
  errorMessages,
  successMessages,
  variablesValues,
} from '../../../../../data/constants';
import { showError } from '../../../../../utils/ui';
import {
  emailFieldNames,
  initialValues,
  textareaFields,
  validationSchemaEmail,
} from '../../../../../lib/mocks/email-mock';
import { addVariablesInText } from '../../../../../utils/utils';

export const EmailForm = ({
  client = null,
  requestId = null,
  initialClientId = null,
  inputs = [],
  handleClose = false,
}) => {
  const [newInitialValues] = useState({ ...initialValues });
  const [inputsTextareas, setInputsTextareas] = useState(textareaFields);
  const [clientId, setClientId] = useState(initialClientId);

  const { selectedRealties } = useEmailContext();

  const userToken = useSelector(selectUserToken);
  const userInfo = useSelector(selectUserInfo);
  const {
    data: clientData = {},
    isFetching: isClientDataLoading,
    error: clientDataError,
  } = useGetClientQuery(
    { token: userToken, id: clientId },
    { skip: !clientId || client, refetchOnMountOrArgChange: true }
  );
  const [sendMailing] = useSendMailingMutation();

  const handleSubmit = async values => {
    const clientInfo = client ?? clientData;
    const resolvedClientId = client?.id ?? clientId;

    const newValues = await addVariablesInText(values, clientInfo, userInfo, variablesValues);

    const formData = new FormData();
    formData.append('subject', newValues[emailFieldNames.subject] ?? '');
    formData.append('title', newValues[emailFieldNames.title] ?? '');
    formData.append('text', newValues[emailFieldNames.text] ?? '');
    if (resolvedClientId) {
      formData.append('client_ids[0]', resolvedClientId);
    }
    if (newValues[emailFieldNames.cc]) {
      formData.append('cc', newValues[emailFieldNames.cc]);
    }
    const attachments = values[emailFieldNames.attachments] ?? [];
    attachments.forEach(file => formData.append('attachments[]', file));
    selectedRealties?.forEach((realty, index) => {
      formData.append(`realty_ids[${index}]`, realty.id);
    });

    const response = await sendMailing({ token: userToken, data: formData });

    if (response?.error || response?.errors) {
      showError(
        response?.error?.status === 422
          ? response.error.data?.message
          : errorMessages.wentWrong
      );
    } else {
      toast.success(successMessages.emailSent, { action: {} });
      if (handleClose) handleClose();
    }
  };

  return (
    <div className="relative flex flex-wrap gap-4 w-full exclude-click dialog-with-click">
      {isClientDataLoading && <LoaderProgress />}

      <EmailVariables />

      <FormTemplate
        initialValues={newInitialValues}
        validationSchema={Yup.object(validationSchemaEmail)}
        handleSubmitForm={handleSubmit}
        isWithoutButton
      >
        {formProps => (
          <EmailFormInputs
            formProps={formProps}
            inputs={inputs}
            inputsTextareas={inputsTextareas}
            setInputsTextareas={setInputsTextareas}
            client={client}
            clientId={clientId}
            setClientId={setClientId}
            requestId={requestId}
            clientData={clientData}
            isClientDataLoading={isClientDataLoading}
            clientDataError={clientDataError}
          />
        )}
      </FormTemplate>
    </div>
  );
};
